/**
 * Users page: CRUD template components + our API.
 * Table only; row click opens user detail drawer. No delete column.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as api from '../../modules/api';
import PageContainer from '../mui-crud/components/PageContainer';
import UserDetailDrawer from './UserDetailDrawer';
import DataGridTableToolbar from '../components/DataGridTableToolbar';
import useDataGridLocaleText from '../hooks/useDataGridLocaleText';
import { getApiErrorMessage } from '../utils/apiErrors';

function mapUsersToRows(users) {
  if (!Array.isArray(users)) return [];
  return users.map((u, index) => ({
    id: u.id || u.phoneNumber || `row-${index}`,
    phoneNumber: u.phoneNumber ?? '—',
    email: u.email ?? '—',
    blocking: Boolean(u.blocking),
  }));
}

export default function UsersPage() {
  const { t } = useTranslation();
  const dataGridLocaleText = useDataGridLocaleText();
  const [rows, setRows] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userCount, setUserCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState(null);

  const loadPage = useCallback(async (page, pageSize = 20) => {
    setError(null);
    setLoading(true);
    try {
      const { data } = await api.getUsersList(page, pageSize);
      const list = data.users || [];
      setRows(mapUsersToRows(list));
      const map = {};
      list.forEach((u) => {
        const key = u.id || u.phoneNumber;
        if (key) map[key] = u;
      });
      setUsersMap(map);
      setUserCount(Number(data.userCount) || 0);
    } catch (e) {
      setError(e?.message || t('users.errorLoad'));
      setRows([]);
      setUsersMap({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    try {
      if (sessionStorage.getItem('crossLinkUser')) return;
    } catch (_) {}
    // Backend PageRequest is 0-based (same as Stations/Powerbanks).
    loadPage(paginationModel.page, paginationModel.pageSize);
  }, [paginationModel.page, paginationModel.pageSize, loadPage]);

  // Open user from cross-link (e.g. from Rent detail "Open in Users")
  useEffect(() => {
    let key = 'crossLinkUser';
    try {
      const identifier = sessionStorage.getItem(key);
      if (!identifier) return;
      sessionStorage.removeItem(key);
      setError(null);
      setLoading(true);
      api
        .FindUsers(identifier)
        .then((res) => {
          const data = res?.data;
          const list = data != null ? (Array.isArray(data) ? data : [data]) : [];
          const user = list[0];
          if (user) {
            const map = {};
            list.forEach((u) => {
              const k = u.id || u.phoneNumber;
              if (k) map[k] = u;
            });
            setRows(mapUsersToRows(list));
            setUsersMap(map);
            setUserCount(list.length);
            setSelectedUser(user);
            setDetailOpen(true);
          }
        })
        .catch((e) => {
          setError(getApiErrorMessage(e, t));
        })
        .finally(() => setLoading(false));
    } catch (_) {}
  }, []);

  const handleRefresh = useCallback(() => {
    if (!loading) loadPage(paginationModel.page, paginationModel.pageSize);
  }, [loading, loadPage, paginationModel.page, paginationModel.pageSize]);

  const handleUserSearch = useCallback(
    (query) => {
      const q = (query ?? searchInputValue ?? '').toString().trim();
      if (!q) {
        setSearchQuery(null);
        setSearchInputValue('');
        loadPage(0, paginationModel.pageSize);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        return;
      }
      setError(null);
      setLoading(true);
      setSearchQuery(q);
      api
        .FindUsers(q)
        .then((res) => {
          const data = res?.data;
          const list = data != null ? (Array.isArray(data) ? data : [data]) : [];
          setRows(mapUsersToRows(list));
          const map = {};
          list.forEach((u) => {
            const key = u.id || u.phoneNumber;
            if (key) map[key] = u;
          });
          setUsersMap(map);
          setUserCount(list.length);
        })
        .catch((e) => {
          setError(getApiErrorMessage(e, t));
          setRows([]);
          setUsersMap({});
          setUserCount(0);
        })
        .finally(() => setLoading(false));
    },
    [searchInputValue, loadPage, paginationModel.pageSize],
  );

  const handleClearSearch = useCallback(() => {
    setSearchQuery(null);
    setSearchInputValue('');
    setError(null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    loadPage(0, paginationModel.pageSize);
  }, [loadPage, paginationModel.pageSize]);

  const handleRowClick = useCallback(
    (params) => {
      const full = usersMap[params.row.id] || params.row;
      setSelectedUser(full);
      setDetailOpen(true);
    },
    [usersMap],
  );

  const columns = React.useMemo(
    () => [
      { field: 'id', headerName: t('users.columnId'), flex: 0.5, minWidth: 80 },
      { field: 'phoneNumber', headerName: t('users.columnPhone'), flex: 1, minWidth: 120 },
      { field: 'email', headerName: t('users.columnEmail'), flex: 1, minWidth: 180 },
      {
        field: 'blocking',
        headerName: t('users.columnStatus'),
        flex: 0.5,
        minWidth: 100,
        renderCell: (params) => (
          <Chip
            label={params.value ? t('users.blocked') : t('users.active')}
            color={params.value ? 'default' : 'success'}
            size="small"
          />
        ),
      },
    ],
    [t],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <PageContainer
        title={t('users.title')}
        breadcrumbs={[]}
        actions={
          <Stack direction="row" alignItems="center" spacing={1}>
            {searchQuery != null && (
              <Chip
                label={t('common.clearSearch')}
                onDelete={handleClearSearch}
                size="small"
                sx={{ mr: 0.5 }}
              />
            )}
            <Tooltip title={t('common.reloadData')} placement="right" enterDelay={1000}>
              <div>
                <IconButton size="small" aria-label="refresh" onClick={handleRefresh}>
                  <RefreshIcon />
                </IconButton>
              </div>
            </Tooltip>
          </Stack>
        }
      >
        <Box sx={{ flex: 1, width: '100%' }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          ) : (
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              rowCount={userCount || rows.length}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 20, 50, 100]}
              onRowClick={handleRowClick}
              localeText={dataGridLocaleText}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
              }
              initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
              disableColumnResize
              density="compact"
              showToolbar
              slots={{ toolbar: DataGridTableToolbar }}
              slotProps={{
                pagination: {
                  labelRowsPerPage: t('common.rowsPerPage'),
                },
                toolbar: {
                  searchValue: searchInputValue,
                  onSearchChange: setSearchInputValue,
                  onSearchSubmit: handleUserSearch,
                  searchPlaceholder: t('users.searchPlaceholder'),
                  loading,
                },
                loadingOverlay: { variant: 'circular-progress', noRowsVariant: 'circular-progress' },
                baseIconButton: { size: 'small' },
              }}
              sx={{
                [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: { outline: 'transparent' },
                [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
                [`& .${gridClasses.row}`]: { cursor: 'pointer' },
              }}
            />
          )}
        </Box>
      </PageContainer>
      <UserDetailDrawer
        user={selectedUser}
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setSelectedUser(null); }}
        onRefresh={handleRefresh}
      />
    </Box>
  );
}
